import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "../../../router/LocaleLink";
import { Paths } from "../../../router/paths";
import { usePaymentPastDue } from "../../../hooks/usePaymentPastDue";
import { trackPaymentFailedBannerViewed } from "../../../analytics/amplitude";
import styles from "./PaymentFailedBanner.module.css";

/**
 * Global "payment failed" bar shown while a renewal is past due
 * (docs/payment-grace-period-design.md). Never states the deadline.
 */
export const PaymentFailedBanner = () => {
  const { t } = useTranslation();
  const { isPastDue, planName, retry, isRetrying } = usePaymentPastDue();
  const tracked = useRef(false);

  useEffect(() => {
    if (!isPastDue || tracked.current) return;
    tracked.current = true;
    trackPaymentFailedBannerViewed();
  }, [isPastDue]);

  if (!isPastDue) return null;

  return (
    <aside
      className={styles.bar}
      role="alert"
      aria-label={t("pages.subscriptions.pastDue.bannerAria")}
    >
      <p className={styles.copy}>
        <span className={styles.headline}>
          {t("pages.subscriptions.pastDue.bannerHeadline")}
        </span>
        <span className={styles.sub}>
          {t("pages.subscriptions.pastDue.bannerSub", { plan: planName })}
        </span>
      </p>
      <span className={styles.actions}>
        <Link to={Paths.SUBSCRIPTIONS} className={styles.cta}>
          {t("pages.subscriptions.pastDue.updateCard")}
        </Link>
        <button
          type="button"
          className={styles.ctaButton}
          onClick={() => void retry("banner")}
          disabled={isRetrying}
        >
          {isRetrying
            ? t("pages.subscriptions.pastDue.retrying")
            : t("pages.subscriptions.pastDue.retryPayment")}
        </button>
      </span>
    </aside>
  );
};
